import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  ReactElement,
} from 'react';

import {Alert, BackHandler, Platform, ScrollView} from 'react-native';
import {RFValue} from 'react-native-responsive-fontsize';
import {useNavigation} from '@react-navigation/native';
import {version} from '../../../../../package.json';
import {SafeAreaView} from 'react-native-safe-area-context';

import {Header} from '../../../Interface/Headers/Header';
import {SpinnerLoading} from '../SpinnerLoading';
import {PedidoDTO} from '../../../../dtos/PedidoDTO';
import {Container, Content} from './styles';
import {useAuth} from '../../../../context/auth';
import {useIdpayVerification} from '../../../../context/IdpayVerification';

import {addInAppTrigger} from '../../../../lib/pushMessaging';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  clearPendingPayment,
  NAV_STATE_KEY,
  savePendingPaymentIfNeeded,
} from '../../../../utils/payments/pendingPayment';
import {api} from '../../../../services/api';
import {getInstallAttributionData} from '../../../../utils/installAttribution';
import {rastrear} from '../../../../services/rastrear';

import {BankAlertModal} from '../../BankAlertModal';
import {UserCreditCardsDTO} from '../../../../dtos/UserCreditCardsDTO';
import {AppEventsLogger} from 'react-native-fbsdk-next';
import analytics from '@react-native-firebase/analytics';
import {trackEvent, trackPurchase} from '../../../../lib/analytics';
import DeviceInfo from 'react-native-device-info';
import {PromoModal} from '../../../Interface/PromoModal';

import {CVCModal} from '../CVCModal';

import {useInstallments} from './hooks/useInstallments';
import {CheckoutDiscountCoupon} from './components/CheckoutDiscountCoupon';
import {CheckoutBaratinhas} from './components/CheckoutBaratinhas';
import {CheckoutCreditCard} from './components/CheckoutCreditCard';
import {CheckoutFooter} from './components/CheckoutFooter';
import {CheckoutPix} from './components/CheckoutPix';
import {CheckoutGetnetLoading} from '../CheckoutGetnetLoading';
import {
  Discount,
  Localidade,
  ModuleInjectedProps,
  ModuleStandardProps,
  PaymentModule,
} from './modules/types';
import {StationDTO} from '../../../../dtos/StationDTO';
import {CouponDTO} from '../../../../dtos/CouponDTO';

import {useStripeGateway} from './gateways/useStripeGateway';
import {useGetnetGateway} from './gateways/useGetnetGateway';
import {useLegacyGateway} from './gateways/useLegacyGateway';
import {usePixGateway} from './gateways/usePixGateway';
import StripeWebview3ds from './3ds/StripeWebview3ds';
import GetnetWebview3ds from './3ds/GetnetWebview3ds';

import {usePaymentMethod} from './hooks/usePaymentMethod';
import {useDiscount} from './hooks/useDiscount';
import {usePaymentGateway} from './hooks/usePaymentGateway';
import {PaymentMethodItemFooterBanners} from '../ChangePaymentMethodItem';
import {useBaratinhas} from '../../../../hooks/useBaratinhas';

export type {Localidade, Discount};

// Fire-and-forget — nunca bloqueia a tela de sucesso do pagamento.
function postConversionEvent(
  value: number,
  eventType: 'purchase_completed' | 'pix_pending',
  reference?: string,
) {
  getInstallAttributionData().then(attribution => {
    api
      .post('/evento_conversao', {
        event_type: eventType,
        value,
        currency: 'BRL',
        reference: reference ?? null,
        nativeDeviceId: attribution?.nativeDeviceId ?? null,
        appsflyer_id: attribution?.appsflyer_id ?? null,
        gclid: attribution?.gclid ?? null,
        fbclid: attribution?.fbclid ?? null,
        attribution_source: attribution?.source ?? 'organic',
        attribution_utm_source: attribution?.utm_source ?? null,
        attribution_campaign: attribution?.utm_campaign ?? null,
        attribution_medium: attribution?.utm_medium ?? null,
        timestamp: new Date().toISOString(),
      })
      .catch(() => {});
  });
}

type Props = {
  module: ReactElement<ModuleStandardProps>;
  enableBaratinhas?: boolean;
  enableDiscounts?: boolean;
  enableVale?: boolean;
  enablePix?: boolean;
  enableCCT?: boolean;
  enableBrbDiscount?: boolean;
  enablePendingPayment?: boolean;
  showReferAndWinButton?: boolean;
  paymentMethodItemFooterBanners?: PaymentMethodItemFooterBanners;
  onGoBack?: () => void;
};

type StandardProps = {
  pedidos: PedidoDTO;
  localidade: Localidade;
  discount: Discount;
  coupon: CouponDTO;
  posto: StationDTO;
  loading: boolean;
  gnv: boolean;
  consulta_id: string;
  tipo_operacao: string;
  operacao_mova_id: string;
  parcela_mova_id: string;
  value: number;
  uids: string[];
  uuid: string;
};

export function CheckoutSession({
  module,
  enableBaratinhas = true,
  enableCCT = true,
  enableVale = true,
  enableDiscounts = true,
  enableBrbDiscount = true,
  enablePendingPayment = true,
  showReferAndWinButton = true,
  enablePix = true,
  onGoBack,
  paymentMethodItemFooterBanners,
}: Props) {
  const {
    pedidos: mPedidos,
    gnv: mGnv,
    discount: mDiscount,
    localidade: mLocalidade,
    coupon: mCoupon,
    posto: mPosto,
    loading: mLoading,
    consulta_id: mConsulta_id,
    tipo_operacao: mTipo_operacao,
    uids: mUids,
    uuid: mUuid,
    value: mValue,
    operacao_mova_id: mOperacao_mova_id,
    parcela_mova_id: mParcela_mova_id,
  } = module.props;

  const DEFAULT_LOCALIDADE = {
    region: null,
  };

  const DATA = {
    pedidos: mPedidos || null,
    gnv: mGnv || false,
    discount: mDiscount || null,
    localidade: mLocalidade || DEFAULT_LOCALIDADE,
    coupon: mCoupon || null,
    posto: mPosto || null,
    loading: mLoading || false,
    consulta_id: mConsulta_id || null,
    uids: mUids || null,
    tipo_operacao: mTipo_operacao || null,
    uuid: mUuid || null,
    value: mValue || null,
    operacao_mova_id: mOperacao_mova_id || null,
    parcela_mova_id: mParcela_mova_id || null,
  } as StandardProps;

  const valorCheckout =
    ((DATA.pedidos && DATA.pedidos.value) || DATA.value) ?? 0;

  const deviceInfo = {
    app_version: version,
    device_model: DeviceInfo.getModel(),
    os: Platform.OS,
    os_version: DeviceInfo.getSystemVersion(),
  };

  const {postCreditCardPayment, postPixPayment} = usePaymentGateway();
  const {selectedPaymentMethod, changePaymentMethod} = usePaymentMethod();

  const {
    usaBaratinhas,
    descontoBaratinhas,
    handleRemoveBaratinhas,
    handleAplicaBaratinhas,
    handleSetDescontoBaratinhas,
  } = useBaratinhas();
  const {isLoadingCoupon} = useDiscount({
    pedidos: DATA.pedidos,
    localidade: DATA.localidade,
    coupon: DATA.coupon,
    discount: DATA.discount,
  });
  const {incentiveUserId, clientId, user} = useAuth();
  const navigation = useNavigation();

  const valorDescontoCodigo = DATA.discount?.value ?? 0;

  const totalValueForInstallmentsCalc =
    valorCheckout - descontoBaratinhas - valorDescontoCodigo;

  // ─── UI state ────────────────────────────────────────────────────────────────
  const [isLoadingCard, setIsLoadingCard] = useState(false);
  const [showAlertaBaratinhas, setShowAlertaBaratinhas] = useState(false);
  const [showCVCModal, setShowCVCModal] = useState(false);
  const [cvcModalKey, setCvcModalKey] = useState(0);
  const [threeDSProvider, setThreeDSProvider] = useState<string | null>(null);

  const contentRef = useRef<ScrollView | null>(null);
  const cardSectionOffsetRef = useRef(0);

  const isSelectedCard =
    selectedPaymentMethod !== null && typeof selectedPaymentMethod !== 'string';
  const selectedCard = isSelectedCard
    ? (selectedPaymentMethod as UserCreditCardsDTO)
    : undefined;
  const isPixSelected = selectedPaymentMethod === 'PIX';

  const cart = useMemo<PedidoDTO[]>(
    () => (DATA.pedidos ? [DATA.pedidos] : []),
    [DATA.pedidos],
  );

  const {
    availableInstallments,
    selectedInstallment,
    setSelectedInstallment,
    isLoadingInstallments,
  } = useInstallments({
    enabled: isSelectedCard,
    value: totalValueForInstallmentsCalc,
    voucher: selectedCard?.voucher,
    onlyOneInstallment: threeDSProvider === 'stripe',
    refreshKey: isSelectedCard ? selectedCard?.id || 'card' : undefined,
    consulta_id: DATA.consulta_id,
    uids: DATA.uids,
    uuid: DATA.uuid,
    tipo_operacao: DATA.tipo_operacao,
  });

  const isDebitoVeicular = DATA.tipo_operacao === 'debito_veicular';
  const isEmprestimo = DATA.tipo_operacao === 'parcela_mova';

  const {refresh: refreshIdpay} = useIdpayVerification();

  // ─── Navigation callbacks ─────────────────────────────────────────────────────
  const onCardSuccess = useCallback(() => {
    AppEventsLogger.logEvent('compra_finalizada');

    trackPurchase({
      value: selectedInstallment?.valorCompraTaxa ?? valorCheckout,
      currency: 'BRL',
      orderId: DATA.uuid,
      coupon: DATA.discount?.code,
    });

    postConversionEvent(
      selectedInstallment?.valorCompraTaxa ?? valorCheckout,
      'purchase_completed',
    );
    rastrear({status_tela: 'cartaoSucesso', cidade: DATA.localidade.cidade});
    handleRemoveBaratinhas();

    if (isDebitoVeicular) {
      refreshIdpay();
    }

    navigation.navigate('ConfirmationScreen', {
      title: 'Pagamento realizado',
      text: isDebitoVeicular
        ? 'Seu pagamento foi aprovado. Acompanhe o status da sua solicitação.'
        : isEmprestimo
        ? 'Pagamento aprovado! Sua parcela do empréstimo será baixada em instantes.'
        : 'Seu pagamento foi aprovado. Em alguns instantes, seus cupons ficarão disponíveis em sua carteira.',
      buttonAction: () =>
        isDebitoVeicular
          ? navigation.navigate('PP_TrackRequests')
          : isEmprestimo
          ? navigation.navigate('Emprestimo')
          : navigation.navigate('Coupons'),
      buttonText: isDebitoVeicular
        ? 'Acompanhar solicitações'
        : isEmprestimo
        ? 'Ir para empréstimos'
        : 'Ir para minha carteira',
      closeIconAction: () =>
        isDebitoVeicular
          ? navigation.navigate('Initial')
          : isEmprestimo
          ? navigation.navigate('Emprestimo')
          : navigation.navigate('Coupons'),
      type: 'success',
    });
  }, [
    navigation,
    handleRemoveBaratinhas,
    DATA.tipo_operacao,
    DATA.uuid,
    DATA.discount,
    selectedInstallment,
    valorCheckout,
    DATA.localidade.cidade,
    isDebitoVeicular,
    refreshIdpay,
  ]);

  const onCardError = useCallback(
    (msg?: string) => {
      rastrear({status_tela: 'cartaoFalha', cidade: DATA.localidade.cidade});
      navigation.navigate('ConfirmationScreen', {
        type: 'error',
        title: 'Pagamento não realizado',
        text:
          msg ||
          'Não foi possível fazer o pagamento com este cartão, tente novamente com outro cartão ou se preferir pode pagar usando PIX.',
        buttonAction: () => navigation.goBack(),
        buttonText: 'Tentar novamente',
        closeIconAction: () => navigation.goBack(),
      });
    },
    [navigation, DATA.localidade.cidade],
  );

  const onLegacyError = useCallback(
    (msg?: string) => {
      rastrear({status_tela: 'cartaoFalha', cidade: DATA.localidade.cidade});
      if (msg) {
        navigation.navigate('ConfirmationScreen', {
          type: 'error',
          title: 'Pagamento não realizado',
          text: msg,
          buttonAction: () => navigation.goBack(),
          buttonText: 'Tentar novamente',
          closeIconAction: () => navigation.goBack(),
        });
        return;
      }
      navigation.navigate('ConfirmationScreen', {
        type: 'error',
        title: 'Pagamento não realizado',
        text: 'Houve algum problema com seu cartão de crédito. Tente escolher outra forma de pagamento ou ligue no seu banco.',
        buttonAction: () => navigation.navigate('Initial'),
        buttonText: 'Voltar para a tela inicial',
        closeIconAction: () => navigation.navigate('Initial'),
      });
    },
    [navigation, DATA.localidade.cidade],
  );

  const onPixSuccess = useCallback(
    (code: string) => {
      postConversionEvent(totalValueForInstallmentsCalc, 'pix_pending', code);

      if (isEmprestimo) {
        navigation.navigate('Pix', {
          code,
          onConfirm: () => navigation.navigate('Emprestimo'),
        });
      } else {
        navigation.navigate('Pix', {code});
      }
    },
    [navigation, isEmprestimo, totalValueForInstallmentsCalc],
  );

  const onPixError = useCallback(
    (msg?: string) => {
      navigation.navigate('ConfirmationScreen', {
        type: 'error',
        title: 'Pedido não realizado',
        text:
          msg ||
          'Ocorreu um erro no processamento. Entre em contato com nosso suporte para obter ajuda.',
        buttonAction: () => navigation.goBack(),
        buttonText: 'Tentar novamente',
        closeIconAction: () => navigation.goBack(),
      });
    },
    [navigation],
  );

  // ─── Centralized API calls ────────────────────────────────────────────────────
  // Base payload for credit card. Gateways extend this via extraPayload.
  const chargeCard = useCallback(
    (extraPayload: Record<string, any>) => {
      if (!selectedCard) {
        return Promise.reject(new Error('Missing card or location'));
      }

      clearPendingPayment();

      const [month, year] = selectedCard.expiration.split('/');
      return postCreditCardPayment({
        versao: version,
        baratinhas: enableBaratinhas ? usaBaratinhas : false,
        incentiveUserId,
        clientId,
        isWebview: Platform.OS === 'web',
        region: DATA.localidade.region,
        value: totalValue,
        number: selectedCard.number,
        expiry: `${month}/${year.slice(2)}`,
        codigoParceiro: DATA.discount?.code,
        placa: DATA.discount?.placa,
        codigo: DATA.discount?.membro,
        parcelas:
          threeDSProvider === 'stripe'
            ? '1'
            : selectedInstallment?.installmentCount.toString() ?? '1',
        pedidos: cart.length > 0 ? cart : null,
        posto_id: DATA.posto?.id ?? '',
        consulta_id: DATA.consulta_id,
        tipo_operacao: DATA.tipo_operacao,
        operacao_mova_id: DATA.operacao_mova_id,
        parcela_mova_id: DATA.parcela_mova_id,
        ...extraPayload,
      });
    },

    [
      selectedCard,
      selectedInstallment,
      valorCheckout,
      usaBaratinhas,
      incentiveUserId,
      clientId,
      DATA,
      cart,
      postCreditCardPayment,
    ],
  );

  // Base payload for PIX. Gateways extend this via extraPayload.
  const chargePix = useCallback(
    (extraPayload?: Record<string, any>) => {
      BackHandler.addEventListener('hardwareBackPress', () => true);

      clearPendingPayment();

      return postPixPayment({
        region: DATA.localidade.region,
        versao: version,
        codigoParceiro: DATA.discount?.code,
        value: totalValue,
        placa: DATA.discount?.placa,
        baratinhas: enableBaratinhas ? usaBaratinhas : false,
        codigo: DATA.discount?.membro,
        pedidos: cart,
        incentiveUserId,
        clientId,
        posto_id: DATA.posto?.id,
        consulta_id: DATA.consulta_id,
        tipo_operacao: DATA.tipo_operacao,
        operacao_mova_id: DATA.operacao_mova_id,
        parcela_mova_id: DATA.parcela_mova_id,
        ...extraPayload,
      });
    },

    [DATA, usaBaratinhas, incentiveUserId, clientId, cart, postPixPayment],
  );

  const stripe = useStripeGateway({
    chargeCard,
    selectedCard,
    onSuccess: onCardSuccess,
    onError: onCardError,
  });

  const getnet = useGetnetGateway({
    chargeCard,
    onSuccess: onCardSuccess,
    onError: onCardError,
  });

  const legacy = useLegacyGateway({
    chargeCard,
    onSuccess: onCardSuccess,
    onError: onLegacyError,
    onRemoveBaratinhas: handleRemoveBaratinhas,
  });

  const pix = usePixGateway({
    chargePix,
    cart,
    isActive: isPixSelected,
    pedidoValue: valorCheckout,
    couponCode: DATA.discount?.code,
    onSuccess: onPixSuccess,
    onError: onPixError,
  });

  // ─── Derived values (depend on pix.taxaPix) ───────────────────────────────────
  const basePixValueWithTax = useMemo(
    () =>
      valorCheckout + pix.taxaPix - descontoBaratinhas - valorDescontoCodigo,
    [valorCheckout, pix.taxaPix, descontoBaratinhas, valorDescontoCodigo],
  );

  const totalValue = useMemo(() => {
    if (isPixSelected) {
      return basePixValueWithTax;
    }
    // valorCompraTaxa already incorporates discounts (it's calculated from
    // totalValueForInstallmentsCalc which has baratinhas/coupon subtracted).
    // Fall back to manually discounted value only when no installment is selected.
    return (
      selectedInstallment?.valorCompraTaxa ??
      valorCheckout - descontoBaratinhas - valorDescontoCodigo
    );
  }, [
    valorCheckout,
    basePixValueWithTax,
    selectedInstallment,
    isPixSelected,
    descontoBaratinhas,
    valorDescontoCodigo,
  ]);

  const serviceTaxIsCard = isSelectedCard;

  const serviceTaxValue = useMemo(
    () => (serviceTaxIsCard ? selectedInstallment?.CET ?? 0 : pix.taxaPix ?? 0),
    [serviceTaxIsCard, selectedInstallment?.CET, pix.taxaPix],
  );

  // ─── Loading state ────────────────────────────────────────────────────────────
  const isLoading = useMemo(
    () =>
      pix.isLoadingTax ||
      pix.isLoadingCashback ||
      isLoadingCoupon ||
      isLoadingCard ||
      pix.isPaying ||
      getnet.isPaying ||
      stripe.isPaying ||
      legacy.isPaying ||
      !!DATA.loading,
    [
      pix.isLoadingTax,
      pix.isLoadingCashback,
      isLoadingCoupon,
      isLoadingCard,
      pix.isPaying,
      getnet.isPaying,
      stripe.isPaying,
      legacy.isPaying,
      DATA.loading,
    ],
  );

  // ─── Side effects ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enablePendingPayment) {
      clearPendingPayment();
      return;
    }
    AsyncStorage.getItem(NAV_STATE_KEY).then(raw => {
      if (raw) {
        savePendingPaymentIfNeeded(raw);
      }
    });
    return () => {
      clearPendingPayment();
    };
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      addInAppTrigger('screen', 'checkout');
    }
  }, []);

  useEffect(() => {
    AppEventsLogger.logEvent('finalizacao_de_compra');
  }, []);

  useEffect(() => {
    if (enablePix && selectedPaymentMethod === null) {
      changePaymentMethod('PIX');
    }
  }, []);

  useEffect(() => {
    return () => {
      stripe.clearPolling();
      getnet.clearPolling();
      legacy.clearPolling();
    };
  }, [stripe.clearPolling, getnet.clearPolling, legacy.clearPolling]);

  useEffect(() => {
    if (!stripe.webviewUri) {
      return;
    }
    analytics().logEvent('checkout_desafio_3ds', {
      user_id: user?.user_id,
      tipo_operacao: DATA.tipo_operacao,
      consulta_id: DATA.consulta_id,
      provider: 'stripe',
      ...deviceInfo,
    });
  }, [stripe.webviewUri]);

  useEffect(() => {
    if (!getnet.showChallenge) {
      return;
    }
    analytics().logEvent('checkout_desafio_3ds', {
      user_id: user?.user_id,
      tipo_operacao: DATA.tipo_operacao,
      consulta_id: DATA.consulta_id,
      provider: 'getnet',
      ...deviceInfo,
    });
  }, [getnet.showChallenge]);

  useEffect(() => {
    if (!selectedCard?.id) {
      return;
    }
    analytics().logEvent('checkout_selecionou_cartao', {
      user_id: user?.user_id,
      tipo_operacao: DATA.tipo_operacao,
      consulta_id: DATA.consulta_id,
      ...deviceInfo,
    });
  }, [selectedCard?.id]);

  useEffect(() => {
    if (!selectedInstallment) {
      return;
    }
    analytics().logEvent('checkout_selecionou_parcela', {
      user_id: user?.user_id,
      tipo_operacao: DATA.tipo_operacao,
      consulta_id: DATA.consulta_id,
      parcelas: selectedInstallment.installmentCount,
      valor_total: totalValue,
      ...deviceInfo,
    });
  }, [selectedInstallment]);

  // ─── Handlers ─────────────────────────────────────────────────────────────────
  async function handleCardPaymentRedirect() {
    setIsLoadingCard(true);

    if (!selectedCard || !selectedInstallment) {
      Alert.alert(
        'Selecione o parcelamento',
        'Escolha uma opção de parcelamento antes de continuar.',
      );
      setIsLoadingCard(false);
      return;
    }

    await api
      .get('/verificaTrocarSenha?tipo_operacao=' + DATA.tipo_operacao)
      .then(async ({data}) => {
        if (data.trocaSenha) {
          navigation.navigate('ChangePassword');
        } else {
          if (!selectedCard?.endereco) {
            const action = () => {
              setCvcModalKey(k => k + 1);
              setShowCVCModal(true);
              navigation.goBack();
            };
            return navigation.navigate('EditCreditCardAddress', {
              action,
              selectedCard,
            });
          }
          setIsLoadingCard(false);
          setCvcModalKey(k => k + 1);
          setShowCVCModal(true);
          analytics().logEvent('checkout_abriu_cvc_modal', {
            user_id: user?.user_id,
            tipo_operacao: DATA.tipo_operacao,
            consulta_id: DATA.consulta_id,
            ...deviceInfo,
          });
        }
      })
      .catch(() => {
        Alert.alert(
          'Ocorreu um problema',
          'Não foi possível selecionar o método de pagamento, tente novamente mais tarde',
        );
      })
      .finally(() => {
        setIsLoadingCard(false);
      });
  }

  async function handleCVCConfirm(cvc: string) {
    setShowCVCModal(false);
    if (threeDSProvider === 'stripe') {
      await stripe.pay(cvc);
      return;
    }
    if (threeDSProvider === 'getnet') {
      await getnet.pay(cvc);
      return;
    }
    await legacy.pay(cvc);
  }

  function handleBaratinhasAlerta() {
    setShowAlertaBaratinhas(false);
    if (isPixSelected) {
      pix.handleRedirect();
    } else {
      handleCardPaymentRedirect();
    }
  }

  // ─── Payment button ───────────────────────────────────────────────────────────
  const isStringMethod = typeof selectedPaymentMethod === 'string';
  const payBtnMap: Record<
    string,
    {title: string; onPress: () => void | Promise<void>}
  > = {
    PIX: {
      title: 'Pagar com PIX',
      onPress: () => {
        pix.handleRedirect();
      },
    },
    TICKETLOG: {title: 'Pagar com Cartão', onPress: handleCardPaymentRedirect},
  };
  const payBtn = isStringMethod
    ? payBtnMap[selectedPaymentMethod] ?? null
    : null;

  const hasPaymentMethodSelected = selectedPaymentMethod !== null;
  const isStringMethodReady =
    !pix.showBankAlert &&
    !showAlertaBaratinhas &&
    (isPixSelected || !!selectedInstallment);
  const isCardMethodReady = !!selectedInstallment && !isLoadingCard;
  const isPaymentMethodReady = isStringMethod
    ? isStringMethodReady
    : isCardMethodReady;
  const canPay = !isLoading && hasPaymentMethodSelected && isPaymentMethodReady;

  // ─── Module injection ─────────────────────────────────────────────────────────
  const ModuleType = module.type as PaymentModule;
  const moduleInjected: ModuleStandardProps & ModuleInjectedProps = {
    ...module.props,
    totalValue,
    isLoading,
    cashback: pix.cashback,
    serviceTaxValue,
    serviceTaxIsCard,
    installment: selectedInstallment,
    baseValue: valorCheckout,
    usaBaratinhas,
    descontoBaratinhas,
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      {isLoading && !stripe.webviewUri && <SpinnerLoading />}

      <CheckoutGetnetLoading
        visible={getnet.isPolling || stripe.isPolling || legacy.isPolling}
      />

      <StripeWebview3ds
        uri={stripe.webviewUri}
        status={stripe.paymentStatus}
        onCancel={stripe.onWebviewCancel}
        onLoaded={stripe.onWebviewLoaded}
      />

      {getnet.challengeData?.result?.redirect_html_template &&
        getnet.showChallenge && (
          <GetnetWebview3ds
            html={getnet.challengeData.result.redirect_html_template}
            isOpen={getnet.showChallenge}
            onCancel={getnet.onChallengeCancel}
            onNavigationStateChange={getnet.onChallengeNavChange}
            ds_trans_id={getnet.challengeData.result.ds_trans_id}
          />
        )}

      <SafeAreaView style={{flex: 1}} edges={['bottom']}>
        <Container>
          {onGoBack ? (
            <Header icon="chevron-left" parentHelp action={onGoBack} />
          ) : (
            <Header icon="chevron-left" parentHelp />
          )}

          <Content
            //@ts-ignore
            ref={contentRef}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{paddingBottom: RFValue(16)}}>
            <ModuleType.Header {...moduleInjected} />
            <ModuleType.Resume {...moduleInjected} />

            {enableDiscounts && !DATA.coupon && (
              <CheckoutDiscountCoupon
                discount={DATA.discount}
                localidade={DATA.localidade}
                pedidos={DATA.pedidos}
                isGnv={DATA.gnv}
              />
            )}

            {enableBaratinhas && (
              <CheckoutBaratinhas
                usaBaratinhas={usaBaratinhas}
                descontoBaratinhas={descontoBaratinhas}
                handleAplicaBaratinhas={handleAplicaBaratinhas}
                handleRemoveBaratinhas={handleRemoveBaratinhas}
                handleSetDescontoBaratinhas={handleSetDescontoBaratinhas}
              />
            )}

            {enablePix && (
              <CheckoutPix
                selectedPaymentMethod={selectedPaymentMethod}
                changePaymentMethod={changePaymentMethod}
              />
            )}

            {enableCCT && (
              <CheckoutCreditCard
                onLayout={(e: any) => {
                  cardSectionOffsetRef.current = e.nativeEvent.layout.y;
                }}
                tipo_operacao={DATA.tipo_operacao}
                pedidos={DATA.pedidos}
                localidade={DATA.localidade}
                enableBrbDiscount={enableBrbDiscount}
                enableVale={enableVale}
                availableInstallments={availableInstallments}
                selectedInstallment={selectedInstallment}
                setSelectedInstallment={setSelectedInstallment}
                isLoadingInstallments={isLoadingInstallments}
                on3DSProviderChange={setThreeDSProvider}
                banners={paymentMethodItemFooterBanners}
                onLoadingChange={setIsLoadingCard}
                onScrollToInstallments={y => {
                  contentRef.current?.scrollTo({
                    y: Math.max(
                      cardSectionOffsetRef.current + y - RFValue(12),
                      0,
                    ),
                    animated: true,
                  });
                }}
                selectedPaymentMethod={selectedPaymentMethod}
                changePaymentMethod={changePaymentMethod}
                autoSelectFirst={!enablePix}
              />
            )}

            {enableBaratinhas && showAlertaBaratinhas && (
              <PromoModal
                isVisible
                buttonAction={handleBaratinhasAlerta}
                buttonText="Confirmar"
                text="Ao finalizar o pedido, suas baratinhaZ serão descontadas automaticamente. Essa operação é irreversível."
                title="Confirmação necessária"
                onDismiss={() => setShowAlertaBaratinhas(false)}
                headline="Você tem certeza?"
              />
            )}

            {enablePix && pix.showBankAlert && (
              <BankAlertModal
                isVisible
                isLoading={pix.isLoadingBanks}
                data={pix.banks}
                onDismiss={() => pix.setShowBankAlert(false)}
                buttonAction={bank => pix.pay(bank)}
              />
            )}

            {enableCCT && (
              <CVCModal
                key={cvcModalKey}
                showModal={showCVCModal && !!selectedCard}
                onDismiss={() => setShowCVCModal(false)}
                cardInfo={
                  selectedCard
                    ? {
                        brandImage: selectedCard.icon,
                        numberMasked: `.... ${selectedCard.number.substring(
                          12,
                          16,
                        )}`,
                        voucher: selectedCard.voucher,
                      }
                    : {brandImage: '', numberMasked: '', voucher: null}
                }
                onConfirm={handleCVCConfirm}
                isLoading={legacy.isPaying}
                brand={selectedCard?.brand}
                buttonText={
                  threeDSProvider === 'getnet' || threeDSProvider === 'stripe'
                    ? 'Continuar'
                    : 'Confirmar compra'
                }
              />
            )}
          </Content>

          <CheckoutFooter
            title={payBtn?.title ?? 'Pagar com cartão'}
            showReferAndWinButton={showReferAndWinButton}
            enabled={canPay}
            tipoOperacao={DATA.tipo_operacao}
            productName={DATA.pedidos?.name}
            onPayButtonPress={() => {
              trackEvent('checkout_clicou_pagar', {
                user_id: user?.user_id,
                tipo_operacao: DATA.tipo_operacao,
                consulta_id: DATA.consulta_id,
                parcelas: selectedInstallment?.installmentCount ?? 1,
                valor_total: totalValue,
                ...deviceInfo,
              });
              if (usaBaratinhas) {
                setShowAlertaBaratinhas(true);
              } else if (payBtn) {
                payBtn.onPress();
              } else {
                handleCardPaymentRedirect();
              }
            }}
          />
        </Container>
      </SafeAreaView>
    </>
  );
}

//TO-DO: Debitos nao estao vindo marcados com todos.
//TO-DO: Selecionar primeiro cartão caso nao tenha meio de pagaemtno pix.
//to-do parcela nao parcelou -> getnets.
